import React, { useEffect, useMemo, useState } from 'react';
import T from 'i18n';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { Modal } from 'features/common/MaterialBasedComponents';
import { Spinner } from 'features/common';
import { fetchExhibitChanges } from '../../redux/fetchExhibitChanges';

const TimelineDot = ({ isLast }) => (
  <div style={{ position: 'relative', width: '24px', display: 'flex', justifyContent: 'center' }}>
    <span
      style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: '#4F46E5',
        marginTop: '4px',
        zIndex: 1,
      }}
    />
    {!isLast && (
      <div
        style={{
          position: 'absolute',
          top: '16px',
          bottom: '-12px',
          width: '2px',
          background: '#E0E0E0',
        }}
      />
    )}
  </div>
);

const TimelineChangeRow = ({ change }) => (
  <div
    style={{
      marginTop: '0.35rem',
      padding: '0.4rem 0.6rem',
      background: '#f7f8fb',
      borderRadius: '6px',
      fontSize: '0.9rem',
      lineHeight: 1.4,
    }}
  >
    <div style={{ fontWeight: 600 }}>
      {T.translate('case.exhibitChangesChangeRow', {
        oldReference: change.oldReference,
        newReference: change.newReference,
        documentId: change.documentId,
        paragraphIndex: change.paragraphIndex,
        estimatedPage: change.estimatedPage,
      })}
    </div>
  </div>
);

const TimelineItem = ({ run, isLast }) => {
  const processedAt = moment(run.processedAt);
  const processedWhen = processedAt.isValid() ? processedAt.fromNow() : '-';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1.6rem' }}>
      <TimelineDot isLast={isLast} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: '0.2rem' }}>
          {T.translate('case.exhibitChangesRunMeta', {
            when: processedWhen,
            triggerType: run.triggerType || 'manual',
          })}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.25rem' }}>
          {T.translate('case.exhibitChangesCounts', {
            updated: run.referencesUpdated,
            found: run.referencesFound,
          })}
        </div>
        {Array.isArray(run.changes) && run.changes.length > 0 && (
          <div style={{ marginTop: '0.4rem', display: 'grid', gap: '0.4rem' }}>
            {run.changes.map((change, idx) => (
              <TimelineChangeRow key={`${run.runId}-change-${idx}`} change={change} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ModalExhibitChanges = ({ show, handleClose, handleSave, action, selectedRows }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const hasSelection = Boolean(selectedRows && selectedRows.length);
  const file = hasSelection ? selectedRows[0] : null;
  const fileName = file?.name || file?.originalName || '' || '';
  const fileType = (file?.type || '').toLowerCase();
  const fileExtension = (fileName.split('.').pop() || '').toLowerCase();
  const isDocxFile = fileType === 'docx' || fileExtension === 'docx';
  const dispatch = useDispatch();

  useEffect(() => {
    if (!show || !isDocxFile || !file?.id) return;
    setLoading(true);
    setError(null);
    setData(null);
    dispatch(fetchExhibitChanges(file.id))
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        setError(err || true);
      })
      .finally(() => setLoading(false));
  }, [show, isDocxFile, file?.id, dispatch]);

  const runs = useMemo(() => {
    if (!data || !Array.isArray(data.processingRuns)) return [];
    return [...data.processingRuns].sort(
      (a, b) => new Date(b.processedAt).getTime() - new Date(a.processedAt).getTime(),
    );
  }, [data]);

  const content = () => {
    if (!hasSelection) return <div>{T.translate('generic.noFilesSelected')}</div>;
    if (!isDocxFile) return <div>{T.translate('case.updateReferencesDocxOnly')}</div>;
    if (loading) return <Spinner />;
    if (error) return <div>{T.translate('case.exhibitChangesError')}</div>;
    if (!runs.length) return <div>{T.translate('case.exhibitChangesEmpty')}</div>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{data?.fileName || fileName}</div>
        <div style={{ color: '#6b7280', fontSize: '0.95rem' }}>
          {T.translate('case.exhibitChangesDescription')}
        </div>
        <div style={{ marginTop: '0.5rem', display: 'grid', gap: '0.5rem' }}>
          {runs.map((run, idx) => (
            <TimelineItem key={run.runId || idx} run={run} isLast={idx === runs.length - 1} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      title={T.translate('generic.exhibitChanges')}
      handleClose={handleClose}
      handleSave={null}
      fullHeight
    >
      <div style={{ maxHeight: '80vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
        {content()}
      </div>
    </Modal>
  );
};

export default ModalExhibitChanges;
